// Offline unlock (REQ-OFF-08): without signal the app unlocks with the phone's own Face ID or
// fingerprint, using the passkey registered for this user; the server confirms the user at sync.
import type { Me } from "../auth/AuthProvider";

const KEY = "rk-offline-user";
export type OfflineUser = { me: Me; credentialIds: string[]; rpId: string; savedAt: string };

export function saveOfflineUser(me: Me, credentialIds: string[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ me, credentialIds, rpId: location.hostname, savedAt: new Date().toISOString() } satisfies OfflineUser));
  } catch { /* storage full or blocked: offline unlock is then unavailable */ }
}

export function offlineUser(): OfflineUser | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as OfflineUser) : null;
  } catch { return null; }
}

function b64urlToBytes(s: string): Uint8Array<ArrayBuffer> {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(s.length / 4) * 4, "=");
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** The authenticator data's flags: bit 0 user present, bit 2 user verified (Face ID or fingerprint). */
function userVerified(authenticatorData: ArrayBuffer): boolean {
  const flags = new Uint8Array(authenticatorData)[32] ?? 0;
  return (flags & 0x01) === 0x01 && (flags & 0x04) === 0x04;
}

export async function unlockOffline(user: OfflineUser): Promise<boolean> {
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const cred = (await navigator.credentials.get({
    publicKey: {
      challenge,
      rpId: user.rpId,
      userVerification: "required",
      allowCredentials: user.credentialIds.map((id) => ({ id: b64urlToBytes(id), type: "public-key" as const })),
      timeout: 60_000
    }
  })) as PublicKeyCredential | null;
  if (!cred) return false;
  const resp = cred.response as AuthenticatorAssertionResponse;
  return user.credentialIds.includes(cred.id) && userVerified(resp.authenticatorData);
}
